/**
 * The hero's greeting sentence: "Morning, Andrew. You've got 4 overdue tasks
 * and 2 intakes to review." built from the summary's `greeting_counts`.
 */
import type {
  ClientGreetingCounts,
  DashboardMode,
  PracticeGreetingCounts
} from "./types";

export const timeOfDay = (now: Date = new Date()): string => {
  const h = now.getHours();
  if (h < 5) return "Evening";
  if (h < 12) return "Morning";
  if (h < 17) return "Afternoon";
  return "Evening";
};

/** "Morning Briefing" / "Afternoon Briefing" / "Evening Briefing" by local time. */
export const briefingTitle = (now: Date = new Date()): string =>
  `${timeOfDay(now)} Briefing`;

export const salutation = (
  firstName: string | null | undefined,
  now: Date = new Date()
): string => {
  const name = (firstName ?? "").trim();
  const tod = timeOfDay(now);
  return name ? `${tod}, ${name}.` : `${tod}.`;
};

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

const joinList = (parts: string[]): string => {
  if (parts.length <= 1) return parts.join("");
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
};

/**
 * The "what's on your plate" clause, or null when nothing needs a look.
 * Practice: "You've got 4 overdue tasks and 2 intakes to review."
 * Client: "Maria has 1 overdue reminder and 1 expired document."
 */
export const greetingSummary = (
  mode: DashboardMode,
  counts: PracticeGreetingCounts | ClientGreetingCounts | null | undefined,
  subject?: string | null
): string | null => {
  if (!counts) return null;
  const parts: string[] = [];
  const shared = (c: {
    approvals_pending: number;
    mentions: number;
    intakes_awaiting_review: number;
    deadlines_overdue: number;
    deadlines_this_week: number;
    expired: number;
    expiring_soon: number;
  }) => {
    if (c.approvals_pending)
      parts.push(
        plural(c.approvals_pending, "approval waiting", "approvals waiting")
      );
    if (c.mentions) parts.push(plural(c.mentions, "unread mention"));
    if (c.intakes_awaiting_review)
      parts.push(
        plural(
          c.intakes_awaiting_review,
          "intake to review",
          "intakes to review"
        )
      );
    if (c.deadlines_overdue)
      parts.push(plural(c.deadlines_overdue, "overdue filing deadline"));
    else if (c.deadlines_this_week)
      parts.push(
        plural(
          c.deadlines_this_week,
          "filing deadline this week",
          "filing deadlines this week"
        )
      );
    if (c.expired) parts.push(plural(c.expired, "expired document"));
    else if (c.expiring_soon)
      parts.push(
        plural(
          c.expiring_soon,
          "document expiring soon",
          "documents expiring soon"
        )
      );
  };
  if (mode === "practice") {
    const c = counts as PracticeGreetingCounts;
    if (c.overdue_mine) parts.push(plural(c.overdue_mine, "overdue task"));
    if (c.due_today_mine)
      parts.push(plural(c.due_today_mine, "task due today", "tasks due today"));
    shared(c);
  } else {
    const c = counts as ClientGreetingCounts;
    if (c.overdue) parts.push(plural(c.overdue, "overdue reminder"));
    if (c.due_today)
      parts.push(
        plural(c.due_today, "reminder due today", "reminders due today")
      );
    shared(c);
  }
  if (!parts.length) return null;
  const shown = parts.slice(0, 3);
  return mode === "practice"
    ? `You've got ${joinList(shown)}.`
    : `${(subject ?? "").trim() || "This client"} has ${joinList(shown)}.`;
};

/** "Tuesday, September 8". */
export const longDate = (now: Date = new Date()): string =>
  now.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric"
  });
